import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import { api } from '../../services/api';
import { FiPhone, FiCheckCircle, FiRefreshCw, FiClock, FiShield } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

export default function PhoneVerification() {
  const { updateUser } = useAuth();
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [status, setStatus] = useState(null); // null, 'pending', 'verified'
  const [countdown, setCountdown] = useState(0);
  const [attemptsRemaining, setAttemptsRemaining] = useState(5);
  const [deliveryMethod, setDeliveryMethod] = useState('sms'); // 'sms' ou 'email'
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState('auto'); // 'auto', 'tn', 'fr'

  // Charger le statut de vérification au montage
  useEffect(() => {
    loadVerificationStatus();
  }, []);

  // Gérer le compte à rebours
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const loadVerificationStatus = async () => {
    try {
      const response = await api.checkPhoneVerificationStatus();
      if (response && response.success) {
        setStatus(response.phone_verified ? 'verified' : 'pending');
        if (response.phone) {
          setPhone(response.phone);
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement du statut:', error);
    }
  };

  // Fonction pour formater automatiquement le numéro avec le préfixe
  const formatPhoneNumber = (value, country) => {
    // Supprimer tous les caractères non numériques sauf +
    let cleaned = value.replace(/[^\d+]/g, '');
    
    // Si commence déjà par +, laisser tel quel
    if (cleaned.startsWith('+')) {
      return cleaned;
    }
    
    // Si commence par 00, convertir en +
    if (cleaned.startsWith('00')) {
      if (cleaned.startsWith('0033')) {
        return '+' + cleaned.substring(2);
      } else if (cleaned.startsWith('00216')) {
        return '+' + cleaned.substring(2);
      }
      return '+' + cleaned.substring(2);
    }
    
    // Détection automatique ou selon le pays sélectionné
    if (country === 'auto') {
      // Détection automatique : 9 chiffres = Tunisie, 10 chiffres = France
      if (cleaned.startsWith('0')) {
        if (cleaned.length === 9) {
          // Numéro tunisien: 012345678 -> +21612345678
          return '+216' + cleaned.substring(1);
        } else if (cleaned.length === 10) {
          // Numéro français: 0612345678 -> +33612345678
          return '+33' + cleaned.substring(1);
        }
      }
      // Si pas de 0 au début mais que c'est un numéro, essayer de détecter
      if (cleaned.length === 8 && /^[2-9]/.test(cleaned)) {
        // Probablement tunisien (8 chiffres sans le 0)
        return '+216' + cleaned;
      } else if (cleaned.length === 9 && /^[1-9]/.test(cleaned)) {
        // Probablement français (9 chiffres sans le 0)
        return '+33' + cleaned;
      }
    } else if (country === 'tn') {
      // Forcer Tunisie
      if (cleaned.startsWith('0')) {
        return '+216' + cleaned.substring(1);
      }
      return '+216' + cleaned;
    } else if (country === 'fr') {
      // Forcer France
      if (cleaned.startsWith('0')) {
        return '+33' + cleaned.substring(1);
      }
      return '+33' + cleaned;
    }
    
    return cleaned;
  };

  // Gestion du changement de numéro avec formatage automatique
  const handlePhoneChange = (e) => {
    const value = e.target.value;
    const formatted = formatPhoneNumber(value, selectedCountry);
    setPhone(formatted);
  };

  const handleSendCode = async (e) => {
    e?.preventDefault();
    
    if (!phone) {
      toast.error('Veuillez entrer un numéro de téléphone');
      return;
    }

    // Validation basique du format (supporte français et tunisien)
    const cleanedPhone = phone.replace(/\s/g, '');
    
    // Format français: +33612345678, 0033612345678, ou 0612345678
    const frenchRegex = /^(\+33|0033|0)[1-9](\d{2}){4}$/;
    // Format tunisien: +21612345678, 0021612345678, ou 012345678
    const tunisianRegex = /^(\+216|00216|0)[2-9]\d{7}$/;
    // Format international général
    const internationalRegex = /^\+[1-9]\d{1,14}$/;
    
    if (!frenchRegex.test(cleanedPhone) && !tunisianRegex.test(cleanedPhone) && !internationalRegex.test(cleanedPhone)) {
      toast.error('Format de numéro invalide. Utilisez le format: +33612345678 (France), +21612345678 (Tunisie), ou 0612345678');
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.sendPhoneVerificationCode(cleanedPhone);
      
      if (response && response.success) {
        // Déterminer la méthode d'envoi
        const method = response.method || 'sms';
        setDeliveryMethod(method);
        
        if (method === 'email') {
          toast.success('Code envoyé par email ! Vérifiez votre boîte de réception.');
          setDeliveryInfo(response.note || 'Le code a été envoyé par email car le service SMS n\'est pas disponible.');
        } else {
          toast.success('Code de vérification envoyé par SMS !');
          setDeliveryInfo(null);
        }
        
        setStatus('pending');
        setCountdown(900); // 15 minutes en secondes
        
        // En mode développement, afficher le code dans la console
        if (response.code && process.env.NODE_ENV === 'development') {
          console.log('🔐 Code de vérification (DEV):', response.code);
          toast.info(`Code DEV: ${response.code}`, { autoClose: 10000 });
        }
      } else {
        toast.error(response?.message || 'Erreur lors de l\'envoi du code');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error?.message || 'Erreur lors de l\'envoi du code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e?.preventDefault();
    
    if (!code || code.length !== 6) {
      toast.error('Veuillez entrer un code à 6 chiffres');
      return;
    }

    setIsVerifying(true);
    try {
      const response = await api.verifyPhoneCode(code);
      
      if (response && response.success) {
        toast.success('Numéro de téléphone vérifié avec succès !');
        setStatus('verified');
        setCode('');
        
        // Mettre à jour l'utilisateur dans le contexte
        if (updateUser) {
          await updateUser();
        }
      } else {
        const message = response?.message || 'Code incorrect';
        toast.error(message);
        
        if (response.attempts_remaining !== undefined) {
          setAttemptsRemaining(response.attempts_remaining);
        }
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error?.message || 'Erreur lors de la vérification');
    } finally {
      setIsVerifying(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Si le téléphone est déjà vérifié
  if (status === 'verified') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-6"
      >
        <div className="flex items-center gap-4">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
              <FiCheckCircle className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">Téléphone vérifié</h3>
            <p className="text-sm text-gray-600 mt-1">
              Votre numéro {phone ? `(${phone})` : ''} a été vérifié avec succès
            </p>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
          <FiShield className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Vérification du téléphone</h3>
          <p className="text-sm text-gray-600">
            Vérifiez votre numéro de téléphone pour accéder à toutes les fonctionnalités
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {/* Champ téléphone */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Numéro de téléphone
          </label>
          
          {/* Sélecteur de pays (optionnel) */}
          <div className="mb-2">
            <select
              value={selectedCountry}
              onChange={(e) => {
                setSelectedCountry(e.target.value);
                if (phone) {
                  // Re-formater le numéro avec le nouveau pays
                  const formatted = formatPhoneNumber(phone.replace(/[^\d+]/g, ''), e.target.value);
                  setPhone(formatted);
                }
              }}
              className="text-xs px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="auto">🌍 Détection automatique</option>
              <option value="tn">🇹🇳 Tunisie (+216)</option>
              <option value="fr">🇫🇷 France (+33)</option>
            </select>
            <span className="text-xs text-gray-500 ml-2">
              {selectedCountry === 'auto' && 'Détecte automatiquement le pays'}
              {selectedCountry === 'tn' && 'Préfixe +216 ajouté automatiquement'}
              {selectedCountry === 'fr' && 'Préfixe +33 ajouté automatiquement'}
            </span>
          </div>

          <div className="flex gap-2">
            <div className="flex-1 relative">
              <FiPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="tel"
                value={phone}
                onChange={handlePhoneChange}
                onBlur={(e) => {
                  // Formater automatiquement quand on quitte le champ
                  if (phone && !phone.startsWith('+')) {
                    const formatted = formatPhoneNumber(phone, selectedCountry);
                    setPhone(formatted);
                  }
                }}
                placeholder={selectedCountry === 'tn' ? '012345678 (devient +21612345678)' : selectedCountry === 'fr' ? '0612345678 (devient +33612345678)' : '012345678 ou 0612345678'}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={isLoading || status === 'pending'}
              />
            </div>
            <button
              onClick={handleSendCode}
              disabled={isLoading || !phone || status === 'pending'}
              className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <FiRefreshCw className="w-4 h-4 animate-spin" />
                  <span>Envoi...</span>
                </>
              ) : (
                <>
                  <FiPhone className="w-4 h-4" />
                  <span>Envoyer le code</span>
                </>
              )}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            💡 Tapez simplement votre numéro (ex: 012345678 ou 0612345678), le préfixe +216 ou +33 sera ajouté automatiquement
          </p>
        </div>

        {/* Champ code de vérification */}
        <AnimatePresence>
          {status === 'pending' && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-2"
            >
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Code de vérification
                {countdown > 0 && (
                  <span className="ml-2 text-xs text-gray-500 flex items-center gap-1">
                    <FiClock className="w-3 h-3" />
                    Expire dans {formatTime(countdown)}
                  </span>
                )}
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={code}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setCode(value);
                  }}
                  placeholder="123456"
                  maxLength={6}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-2xl tracking-widest font-mono"
                />
                <button
                  onClick={handleVerifyCode}
                  disabled={isVerifying || code.length !== 6}
                  className="px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {isVerifying ? (
                    <>
                      <FiRefreshCw className="w-4 h-4 animate-spin" />
                      <span>Vérification...</span>
                    </>
                  ) : (
                    <>
                      <FiCheckCircle className="w-4 h-4" />
                      <span>Vérifier</span>
                    </>
                  )}
                </button>
              </div>
              {attemptsRemaining < 5 && (
                <p className="text-xs text-amber-600">
                  Tentatives restantes: {attemptsRemaining}
                </p>
              )}
              <button
                onClick={handleSendCode}
                disabled={countdown > 0}
                className="text-sm text-blue-600 hover:text-blue-700 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <FiRefreshCw className="w-3 h-3" />
                Renvoyer le code {countdown > 0 && `(${formatTime(countdown)})`}
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Message d'information sur la méthode d'envoi */}
        {deliveryInfo && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-800">
              <strong>ℹ️ Information :</strong> {deliveryInfo}
            </p>
          </div>
        )}

        {/* Message d'information */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Pourquoi vérifier mon téléphone ?</strong>
            <br />
            La vérification de votre numéro de téléphone est obligatoire pour les déménageurs. 
            Cela permet de garantir la sécurité et la fiabilité de notre plateforme.
            {deliveryMethod === 'email' && (
              <>
                <br /><br />
                <strong>Note :</strong> Le code a été envoyé par email. Si vous préférez recevoir des SMS, 
                configurez Twilio Trial (gratuit) dans les paramètres du serveur.
              </>
            )}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

